import { ApiResponse } from '@nf/types/api';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { useApiBackendInfo } from '@/hooks/query/use-api-backend-info';
import { useQuery } from '@tanstack/react-query';
import queryKeys from '@/constants/query-keys';
import { useLicSettings } from '@/domains/config/hooks/use-lic-settings';
import { ApiBackendInfo } from '@/hooks/query/athena/types';
import { WcPromotionPoolResponse } from './types';
import { WC_PROMOTION_AVAILABLE_PRIZE_POOLS_CACHE_MS } from './constants';

const fetchWcPromotionPool = async (
	apiBackendInfo: ApiBackendInfo,
	poolId: string
): Promise<ApiResponse<WcPromotionPoolResponse>> => {
	const response = await fetchComposer.getWithBearer(
		`${apiBackendInfo.url}/WcPromotion/GetPool?poolId=${encodeURIComponent(poolId)}`
	)(apiBackendInfo.token);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}
	return await response.json();
};

/** 獲取單一獎池的詳細資訊, 使用在 weekly lottery 或 after party 中 
 * 等使用者點擊dropdown 中的獎池時, 呼叫這個api取得詳細資訊
*/
export const useWcPromotionPool = (poolId: string) => {
	const { enableWcPromotionLuckySpin, enableWcPromotionWeeklyLottery, enableWcPromotionAfterParty } = useLicSettings();
	const apiBackendInfo = useApiBackendInfo();
	const isWcPromotionEnabled = enableWcPromotionLuckySpin || enableWcPromotionWeeklyLottery || enableWcPromotionAfterParty;

	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [
			queryKeys.WC_PROMOTION_POOL,
			apiBackendInfo.url,
			apiBackendInfo.token,
			poolId
		],
		queryFn: () => fetchWcPromotionPool(apiBackendInfo, poolId),
		staleTime: WC_PROMOTION_AVAILABLE_PRIZE_POOLS_CACHE_MS,
		cacheTime: WC_PROMOTION_AVAILABLE_PRIZE_POOLS_CACHE_MS,
		enabled: isWcPromotionEnabled && apiBackendInfo.isReady && !!poolId
	});

	return {
		data: data?.Data,
		isLoading,
		isError,
		error,
		refetch
	};
};
